import { useState, useRef, useEffect } from "react";
import { Search, ChevronDown, Check, X } from "lucide-react";
import styles from "./CommonDropDown.module.css";

export const CommonMultipleDropdown = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]); // Store selected IDs or strings
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  const isMulti = props?.isMulti ?? true;

  const getOptionText = (option) => {
    return typeof option === "string" ? option : option?.name;
  };

  const getSelectedTexts = () => {
    if (!selectedOptions?.length) return [];
    return props.options
      ?.filter((option) =>
        selectedOptions.includes(typeof option === "string" ? option : option.id)
      )
      .map(getOptionText);
  };

  const filteredOptions = Array.isArray(props?.options)
    ? props.options.filter((option) =>
        getOptionText(option).toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  useEffect(() => {
    setSelectedOptions(Array.isArray(props?.value) ? props.value : []);
  }, [props?.value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => setIsOpen(!isOpen);

  const handleOptionClick = (option) => {
    const value = typeof option === "string" ? option : option?.id;
    let updatedSelections = [];

    if (selectedOptions.includes(value)) {
      updatedSelections = selectedOptions.filter((item) => item !== value);
    } else {
      updatedSelections = [...selectedOptions, value];
    }

    setSelectedOptions(updatedSelections);
    props.onChange({ target: { name: props.name, value: updatedSelections } });
  };

  const clearSelection = () => {
    setSelectedOptions([]);
    setSearchTerm("");
    props.onChange({ target: { name: props.name, value: [] } });
  };

  return (
    <div className={styles.dropdownContainer} ref={dropdownRef}>
      <div
        className={`form-control ${styles.dropdownHeader}`}
        onClick={toggleDropdown}
      >
        <span
          className={
            selectedOptions.length > 0
              ? styles.headerText
              : styles.placeholderTex
          }
        >
          {selectedOptions.length > 0
            ? getSelectedTexts().join(", ")
            : props?.placeholder}
        </span>
        <ChevronDown
          className={`${styles.icon} ${isOpen ? styles.rotated : ""}`}
        />
      </div>

      {isOpen && (
        <div className={styles.dropdownContent}>
          <div className={styles.searchContainer}>
            <Search className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search options..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          <div className={styles.optionsContainer}>
            <ul className={styles.optionsList}>
              <li
                onClick={clearSelection}
                className={`${styles.option} ${
                  selectedOptions.length === 0 ? styles.selected : ""
                }`}
              >
                <span>Clear selection</span>
                {selectedOptions.length === 0 && (
                  <Check className={styles.checkIcon} />
                )}
              </li>

              {filteredOptions.map((option, index) => {
                const optionValue =
                  typeof option === "string" ? option : option.id;
                const isSelected = selectedOptions.includes(optionValue);

                return (
                  <li
                    key={index}
                    onClick={() => handleOptionClick(option)}
                    className={`${styles.option} ${
                      isSelected ? styles.selected : ""
                    }`}
                  >
                    <span>{getOptionText(option)}</span>
                    {isSelected && <Check className={styles.checkIcon} />}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};
